import { useFormik, FormikProvider } from "formik";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { teachersApi } from "../../services/teachers/teachersApi";
import { academicYearsApi } from "../../services/nomenclatures/academicYearsApi";
import CustomDropdown from "../../components/forms/CustomDropdown";
import CustomInputText from "../../components/forms/CustomInputText";
import CustomDatepicker from "../../components/forms/CustomDatepicker";
import {
  CreateRecognitionFromStudentRequest,
  PreviewRecognitionFromStudentResponse,
  ProtocolTeacherTeamRequest,
  StudentInfoAdminResponse,
  TeacherResponse,
} from "../../models/dtos";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { ApiResponse } from "../../services/base/apiResponse";

interface CreateRecognitionFromStudentDialogProps {
  visible: boolean;
  onHide: () => void;
  student: StudentInfoAdminResponse;
}

interface FormValues {
  protocolNumber: string;
  teacherId: number | null;
  substituteTeacherId: number | null;
  academicYearId: number | null;
  issueDate: Date | null;
  protocolTeachers: ProtocolTeacherTeamRequest[];
}

const teacherOptionLabel = (option: TeacherResponse) =>
  `${option.firstName ?? ""} ${option.middleName ?? ""} ${option.lastName ?? ""} - ${option.academicPositionShortName ?? ""} ${option.academicRankShortName ?? ""}`;

const extractErrorMessage = (error: FetchBaseQueryError | SerializedError): string => {
  if ("data" in error && error.data) {
    const data = error.data as ApiResponse<unknown>;
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors.join(", ");
    }
    if (data.message) {
      return data.message;
    }
  }

  if ("message" in error && error.message) {
    return error.message;
  }

  return "Възникна грешка.";
};

const CreateRecognitionFromStudentDialog = ({ visible, onHide, student }: CreateRecognitionFromStudentDialogProps) => {
  const navigate = useNavigate();
  const [sourceFacultyNumber, setSourceFacultyNumber] = useState("");
  const [preview, setPreview] = useState<PreviewRecognitionFromStudentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: teachers } = teachersApi.useGetTeachersQuery({});
  const { data: academicYears } = academicYearsApi.useGetAcademicYearsQuery();
  const { data: currentAcademicYear } = academicYearsApi.useGetCurrentAcademicYearIdQuery();

  const [previewRecognition, { isLoading: isPreviewLoading }] = protocolsApi.usePreviewRecognitionFromStudentMutation();
  const [createRecognition, { isLoading: isCreateLoading }] = protocolsApi.useCreateRecognitionProtocolFromStudentMutation();

  const formik = useFormik<FormValues>({
    initialValues: {
      protocolNumber: "",
      teacherId: null,
      substituteTeacherId: null,
      academicYearId: currentAcademicYear?.data ?? null,
      issueDate: new Date(),
      protocolTeachers: [],
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!preview) {
        setError("Първо заредете оценките от източника по ФН.");
        return;
      }

      if (!values.protocolNumber.trim()) {
        setError("Номерът на протокола е задължителен.");
        return;
      }

      if (!values.teacherId) {
        setError("Преподавателят е задължителен.");
        return;
      }

      if (!values.academicYearId) {
        setError("Академичната година е задължителна.");
        return;
      }

      if (preview.matchedGrades.length === 0) {
        setError("Няма съвпадащи оценки за създаване на протокол.");
        return;
      }

      setError(null);

      const request: CreateRecognitionFromStudentRequest = {
        targetStudentId: student.studentId,
        sourceFacultyNumber: sourceFacultyNumber.trim(),
        protocolNumber: values.protocolNumber.trim(),
        teacherId: values.teacherId,
        substituteTeacherId: values.substituteTeacherId,
        specialityId: student.specialityId,
        issueDate: values.issueDate ? values.issueDate.toISOString() : null,
        academicYearId: values.academicYearId,
        protocolTeachers: values.protocolTeachers.length > 0 ? values.protocolTeachers : null,
      };

      try {
        const result = await createRecognition(request).unwrap();
        if (result.data) {
          handleClose();
          navigate(`/protocols/${result.data}/recognition`);
        } else {
          setError(result.message || "Протоколът не беше създаден.");
        }
      } catch (err) {
        setError(extractErrorMessage(err as FetchBaseQueryError | SerializedError));
      }
    },
  });

  useEffect(() => {
    if (!visible) {
      return;
    }

    setSourceFacultyNumber("");
    setPreview(null);
    setError(null);
    formik.resetForm({
      values: {
        protocolNumber: "",
        teacherId: null,
        substituteTeacherId: null,
        academicYearId: currentAcademicYear?.data ?? null,
        issueDate: new Date(),
        protocolTeachers: [],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when dialog opens
  }, [visible, currentAcademicYear?.data]);

  const availableSubstituteTeachers = (teachers?.data ?? []).filter(
    (teacher) => teacher.teacherId !== formik.values.teacherId,
  );

  const handleClose = () => {
    setSourceFacultyNumber("");
    setPreview(null);
    setError(null);
    formik.resetForm();
    onHide();
  };

  const handlePreview = () => {
    void (async () => {
      setError(null);
      setPreview(null);

      if (!sourceFacultyNumber.trim()) {
        setError("Въведете факултетен номер на източника.");
        return;
      }

      try {
        const result = await previewRecognition({
          targetStudentId: student.studentId,
          sourceFacultyNumber: sourceFacultyNumber.trim(),
        }).unwrap();
        setPreview(result.data);
        if (!result.data.matchedGrades?.length) {
          setError("Няма съвпадащи оценки с учебния план на текущия студент.");
        }
      } catch (err) {
        setError(extractErrorMessage(err as FetchBaseQueryError | SerializedError));
      }
    })();
  };

  const handleAddCommissionMember = () => {
    void formik.setFieldValue("protocolTeachers", [
      ...formik.values.protocolTeachers,
      {
        teacherId: null,
        teacherName: null,
        orderNumber: formik.values.protocolTeachers.length + 1,
      },
    ]);
  };

  const handleCommissionTeacherChange = (index: number, teacherId: number | null) => {
    const updated = [...formik.values.protocolTeachers];
    updated[index] = {
      ...updated[index],
      teacherId,
      teacherName: null,
      orderNumber: index + 1,
    };
    void formik.setFieldValue("protocolTeachers", updated);
  };

  const handleCommissionNameChange = (index: number, teacherName: string) => {
    const updated = [...formik.values.protocolTeachers];
    updated[index] = {
      ...updated[index],
      teacherId: null,
      teacherName,
      orderNumber: index + 1,
    };
    void formik.setFieldValue("protocolTeachers", updated);
  };

  const handleRemoveCommissionMember = (index: number) => {
    const updated = formik.values.protocolTeachers
      .filter((_, i) => i !== index)
      .map((item, idx) => ({ ...item, orderNumber: idx + 1 }));
    void formik.setFieldValue("protocolTeachers", updated);
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button label="Отказ" icon="pi pi-times" onClick={handleClose} className="p-button-text" disabled={isCreateLoading} />
      <Button
        label="Създай протокол"
        icon="pi pi-check"
        onClick={() => {
          void formik.submitForm();
        }}
        loading={isCreateLoading}
        disabled={isCreateLoading || isPreviewLoading || !preview || preview.matchedGrades.length === 0}
      />
    </div>
  );

  const targetStudentName = [student.firstName, student.middleName, student.lastName].filter(Boolean).join(" ");

  return (
    <Dialog
      header="Създаване на протокол за признаване"
      visible={visible}
      onHide={handleClose}
      footer={footer}
      style={{ width: "70vw" }}
      maximizable
    >
      <FormikProvider value={formik}>
        <div className="flex flex-column gap-3">
          {error && <Message severity="error" text={error} />}

          <div className="surface-100 border-round p-3">
            <div className="font-semibold mb-2">Целеви студент</div>
            <div>
              {student.facultyNumber} — {targetStudentName}
            </div>
          </div>

          <div className="flex flex-column md:flex-row gap-2 align-items-end">
            <div className="flex-1 flex flex-column gap-1">
              <label htmlFor="sourceFacultyNumber">ФН на източника</label>
              <InputText
                id="sourceFacultyNumber"
                value={sourceFacultyNumber}
                onChange={(e) => setSourceFacultyNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handlePreview();
                  }
                }}
                placeholder="Въведете факултетен номер"
              />
            </div>
            <Button
              label="Зареди оценки"
              icon="pi pi-search"
              onClick={handlePreview}
              loading={isPreviewLoading}
              disabled={isPreviewLoading || !sourceFacultyNumber.trim()}
            />
          </div>

          {preview && (
            <div className="flex flex-column gap-2">
              <div className="surface-100 border-round p-3">
                <div className="font-semibold mb-2">Студент източник</div>
                <div>
                  {preview.sourceFacultyNumber} — {preview.sourceStudentName}
                </div>
              </div>

              <DataTable
                value={preview.matchedGrades}
                emptyMessage="Няма съвпадащи оценки."
                size="small"
                scrollable
                scrollHeight="240px"
              >
                <Column field="subjectName" header="Дисциплина" />
                <Column field="sourceSemester" header="Сем. (източник)" style={{ width: "8rem" }} />
                <Column field="targetSemester" header="Сем. (цел)" style={{ width: "8rem" }} />
                <Column field="grade" header="Оценка" style={{ width: "6rem" }} />
                <Column field="sourceStudentGradeTypeName" header="Тип оценка" style={{ width: "10rem" }} />
              </DataTable>
            </div>
          )}

          <div className="grid">
            <div className="col-12 md:col-4">
              <CustomInputText id="protocolNumber" label="Номер на протокол" />
            </div>
            <div className="col-12 md:col-4">
              <CustomDropdown
                id="teacherId"
                label="Преподавател"
                options={teachers?.data ?? []}
                optionLabel={teacherOptionLabel}
                optionValue="teacherId"
                filter
              />
            </div>
            <div className="col-12 md:col-4">
              <CustomDropdown
                id="substituteTeacherId"
                label="Преподавател (подписващ)"
                options={availableSubstituteTeachers}
                optionLabel={teacherOptionLabel}
                optionValue="teacherId"
                filter
                showClear
              />
            </div>
            <div className="col-12 md:col-4">
              <CustomDropdown
                id="academicYearId"
                label="Академична година"
                options={academicYears?.data ?? []}
                optionLabel="name"
                optionValue="academicYearId"
              />
            </div>
            <div className="col-12 md:col-4">
              <CustomDatepicker id="issueDate" label="Дата на издаване" />
            </div>
          </div>

          <div className="flex flex-column gap-2">
            <div className="flex align-items-center justify-content-between">
              <label className="font-semibold">Комисия / преподаватели (опционално)</label>
              <Button type="button" icon="pi pi-plus" label="Добави" text onClick={handleAddCommissionMember} />
            </div>
            {formik.values.protocolTeachers.map((member, index) => (
              <div key={index} className="flex flex-row gap-2 align-items-end">
                <div className="flex-1">
                  <CustomDropdown
                    id={`protocolTeacher-${index}`}
                    label={`Преподавател ${index + 1}`}
                    options={teachers?.data ?? []}
                    optionLabel={teacherOptionLabel}
                    optionValue="teacherId"
                    filter
                    value={member.teacherId}
                    onChange={(e: { value: number | null }) => handleCommissionTeacherChange(index, e.value)}
                  />
                </div>
                <div className="flex-1">
                  <CustomInputText
                    id={`protocolTeacherName-${index}`}
                    label="Име (за външни членове)"
                    value={member.teacherName ?? ""}
                    onChange={(e: { target: { value: string } }) => handleCommissionNameChange(index, e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  icon="pi pi-trash"
                  severity="danger"
                  onClick={() => handleRemoveCommissionMember(index)}
                />
              </div>
            ))}
          </div>
        </div>
      </FormikProvider>
    </Dialog>
  );
};

export default CreateRecognitionFromStudentDialog;
